import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Container,
    Card,
    CardContent,
    Button,
    TextField,
    Divider,
    CircularProgress
} from '@mui/material';
import {
    Email,
    Phone,
    LocationOn,
    Schedule
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { emailApi } from '../services/api';
import { useNotification } from '../providers/NotificationProvider';
import { useBackgroundColor } from '@/providers/BackgroundColorProvider';
import { footerContentApi } from '../services/api';
import type { FooterContent } from '../types';
import { useStrapiLocalization } from '@/hooks/useStrapiLocalization';

const Contact: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { showSuccess, showError } = useNotification();
    const { setBackgroundColor } = useBackgroundColor();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [footerContent, setFooterContent] = useState<FooterContent | null>(null);
    const localizedFooterContent = useStrapiLocalization(footerContent, i18n.language);

    useEffect(() => {
        setBackgroundColor('#fff');
        return () => setBackgroundColor('#000');
    }, [setBackgroundColor]);

    useEffect(() => {
        const fetchFooterContent = async () => {
            try {
                const { data, error } = await footerContentApi.get();
                if (!error && data?.data) {
                    setFooterContent(data.data);
                }
            } catch (err) {
                console.error('Error fetching footer content (contact page):', err);
            }
        };

        fetchFooterContent();
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (isSubmitting) return;

        // Validate fields on frontend
        if (!name.trim() || !email.trim() || !message.trim()) {
            showError(t('please_fill_all_fields'));
            return;
        }

        if (message.length < 10) {
            showError(t('message_too_short'));
            return;
        }

        setIsSubmitting(true);

        try {
            const { data, error } = await emailApi.sendContactForm({
                name: name.trim(),
                email: email.trim(),
                message: message.trim()
            });

            if (error) {
                showError(error);
            } else if (data?.success) {
                showSuccess(t('message_sent_success'));
                // Reset form
                setName("");
                setEmail("");
                setMessage("");
            } else {
                showError(data?.error || t('message_send_failed'));
            }
        } catch (err) {
            console.error("Email send error:", err);
            showError(t('connection_error'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const phoneValue = localizedFooterContent?.phone || '';
    const emailValue = localizedFooterContent?.email || '';
    const phoneDisplayValue = localizedFooterContent?.phoneDisplayText || localizedFooterContent?.phone || '';
    const emailDisplayValue = localizedFooterContent?.emailDisplayText || localizedFooterContent?.email || '';

    const telHref = `tel:${phoneValue.replace(/\s+/g, '')}`;
    const mailtoHref = `mailto:${emailValue}`;

    const contactInfo: {
        icon: React.ReactNode;
        title: string;
        content: string;
        action?: () => void;
    }[] = [
        ...(localizedFooterContent?.showEmail !== false
            ? [{
                icon: <Email />,
                title: localizedFooterContent?.contactEmailLabel || '',
                content: emailDisplayValue,
                action: () => window.open(mailtoHref)
            }]
            : []),
        ...(localizedFooterContent?.showPhone !== false
            ? [{
                icon: <Phone />,
                title: localizedFooterContent?.contactPhoneLabel || '',
                content: phoneDisplayValue,
                action: () => window.open(telHref)
            }]
            : []),
        // {
        //     icon: <LocationOn />,
        //     title: t('location'),
        //     content: "New York, NY",
        //     action: () => window.open('https://maps.google.com')
        // },
        // {
        //     icon: <Schedule />,
        //     title: t('hours'),
        //     content: t('business_hours'),
        //     action: undefined
        // }
    ];

    return (
        <Container maxWidth="lg" sx={{ marginTop: '64px' }}>
            <Box
                sx={{
                    padding: { xs: 2, md: 4 },
                    textAlign: 'center'
                }}
            >
                <Typography
                    variant="h3"
                    component="h2"
                    gutterBottom
                    sx={{
                        fontWeight: 'bold',
                        marginBottom: 2,
                        color: 'black'
                    }}
                >
                    {localizedFooterContent?.contactSectionTitle || ''}
                </Typography>

                <Typography
                    variant="h6"
                    component="h3"
                    gutterBottom
                    sx={{
                        fontWeight: 300,
                        marginBottom: 4,
                        color: 'black'
                    }}
                >
                    {localizedFooterContent?.contactSectionSubtitle || ''}
                </Typography>

                <Box
                    sx={{
                        // display: 'grid',
                        // gridTemplateColumns: {
                        //     xs: '1fr',
                        //     sm: 'repeat(2, 1fr)',
                        //     md: 'repeat(4, 1fr)'
                        // },
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'center',
                        alignItems: 'stretch',
                        gap: 4
                    }}
                >
                    {contactInfo.map((info, index) => (
                        <Card
                            key={index}
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                padding: 2,
                                bgcolor: 'transparent',
                                border: 'none',
                                boxShadow: 'none',
                                cursor: info.action ? 'pointer' : 'default',
                                transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
                            }}
                        >
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        width: 60,
                                        height: 60,
                                        borderRadius: '50%',
                                        backgroundColor: 'white',
                                        border: '1px solid black',
                                        color: 'black',
                                        marginBottom: 2,
                                        margin: '0 auto 16px auto',
                                        transition: 'background-color 0.2s ease, color 0.2s ease',
                                        '&:hover': info.action ? {
                                            backgroundColor: 'rgba(0,0,0,0.6)',
                                        } : {},
                                    }}
                                    onClick={info.action}
                                >
                                    {info.icon}
                                </Box>

                                <Typography
                                    variant="h6"
                                    component="h4"
                                    gutterBottom
                                    sx={{ fontWeight: 'bold', color: 'black' }}
                                >
                                    {info.title}
                                </Typography>

                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{ color: 'black' }}
                                >
                                    {info.content}
                                </Typography>
                            </CardContent>
                        </Card>
                    ))}
                </Box>

                <Divider sx={{ marginY: 6 }} />

                {/* Contact Form Section */}
                <Box
                    sx={{
                        marginTop: 6,
                        textAlign: 'center'
                    }}
                >
                    <Typography
                        variant="h3"
                        component="h3"
                        gutterBottom
                        sx={{
                            fontWeight: 'medium',
                            marginBottom: 3,
                            color: 'black'
                        }}
                    >
                        {localizedFooterContent?.contactFormTitle || ''}
                    </Typography>

                    <Typography
                        variant="body1"
                        paragraph
                        sx={{
                            marginBottom: 4,
                            maxWidth: '600px',
                            margin: '0 auto 32px auto',
                            color: 'black'
                        }}
                    >
                        {localizedFooterContent?.contactFormSubtitle || ''}
                    </Typography>

                    <Box
                        sx={{
                            maxWidth: 600,
                            margin: '0 auto',
                            textAlign: 'left',
                        }}
                    >
                        <form onSubmit={handleSubmit}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 3
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 2,
                                        flexDirection: { xs: 'column', sm: 'row' }
                                    }}
                                >
                                    <TextField
                                        fullWidth
                                        label={localizedFooterContent?.contactFormNameLabel || t("name")}
                                        name="name"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        required
                                        variant="outlined"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': { borderColor: 'rgba(0,0,0,0.3)' },
                                                '&:hover fieldset': { borderColor: '#000' },
                                                '&.Mui-focused fieldset': { borderColor: '#000' },
                                            },
                                            '& .MuiInputLabel-root': { color: 'rgba(0,0,0,0.5)' },
                                            '& .MuiInputLabel-root.Mui-focused': { color: '#000' },
                                            '& .MuiInputBase-input': { color: '#000' },
                                        }}
                                    />
                                    <TextField
                                        fullWidth
                                        label={localizedFooterContent?.contactFormEmailLabel || t("email")}
                                        name="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        type="email"
                                        variant="outlined"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': { borderColor: 'rgba(0,0,0,0.3)' },
                                                '&:hover fieldset': { borderColor: '#000' },
                                                '&.Mui-focused fieldset': { borderColor: '#000' },
                                            },
                                            '& .MuiInputLabel-root': { color: 'rgba(0,0,0,0.5)' },
                                            '& .MuiInputLabel-root.Mui-focused': { color: '#000' },
                                            '& .MuiInputBase-input': { color: '#000' },
                                        }}
                                    />
                                </Box>
                                <TextField
                                    fullWidth
                                    label={localizedFooterContent?.contactFormMessageLabel || t("message")}
                                    name="message"
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    required
                                    multiline
                                    rows={6}
                                    variant="outlined"
                                    placeholder={localizedFooterContent?.contactFormMessagePlaceholder || ''}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': { borderColor: 'rgba(0,0,0,0.3)' },
                                            '&:hover fieldset': { borderColor: '#000' },
                                            '&.Mui-focused fieldset': { borderColor: '#000' },
                                        },
                                        '& .MuiInputLabel-root': { color: 'rgba(0,0,0,0.5)' },
                                        '& .MuiInputLabel-root.Mui-focused': { color: '#000' },
                                        '& .MuiInputBase-input': { color: '#000' },
                                        '& .MuiInputBase-inputMultiline': { color: '#000' },
                                    }}
                                />
                                <Box sx={{ textAlign: 'center', marginTop: 2 }}>
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        disabled={isSubmitting}
                                        startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                                        sx={{
                                            backgroundColor: 'rgba(0,0,0,0.3)',
                                            color: '#ffffff',
                                            border: '2px solid rgba(0,0,0,0.3)',
                                            px: 6,
                                            py: 1.5,
                                            fontSize: '1rem',
                                            fontWeight: 600,
                                            letterSpacing: '0.05em',
                                            borderRadius: 0,
                                            textTransform: 'uppercase',
                                            minWidth: '200px',
                                            '&:hover': {
                                                backgroundColor: '#000',
                                                color: '#ffffff',
                                                borderColor: '#000',
                                            },
                                            '&:disabled': {
                                                backgroundColor: 'rgba(0,0,0,0.2)',
                                                color: 'rgba(0,0,0,0.3)',
                                                borderColor: 'rgba(0,0,0,0.2)',
                                            },
                                        }}
                                    >
                                        {isSubmitting
                                            ? (localizedFooterContent?.contactFormSubmittingText || t("please_wait"))
                                            : (localizedFooterContent?.contactFormSubmitText || t("submit"))}
                                    </Button>
                                </Box>
                            </Box>
                        </form>
                    </Box>
                </Box>
            </Box>
        </Container>
    );
};

export default Contact;